import { useHoverIntent } from '@liveauctioneers/hammer-ui-core/hooks/useHoverIntent';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import * as React from 'react';
import isEqual from 'react-fast-compare';

import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useAppDispatch } from '@/redux/hooks';
import { AdUnit } from '@/redux/modules/catalog/ad/catalogAd.types';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import noop from '@/utils/noop';
import { safelyRender } from '@/utils/safelyRender';
import { addImageParams, getAdQuestionUrl, getAuctioneerLogoUrl, getCatalogUrl } from '@/utils/urls';
import useSSRFriendlyDate from '@/utils/useSSRFriendlyDate';

import { RouteButton } from '../RouteButton/RouteButton';

type Props = React.PropsWithChildren<{
    adUnit: AdUnit;
    className: string;
    scrolled: boolean;
    stick: boolean;
    stickBottom: boolean;
    trackClick: (event: React.SyntheticEvent) => void;
}>;

type CatalogAdContentLinkProps = Omit<React.ComponentProps<typeof Link>, 'href'> & {
    href: string;
};

const CatalogAdContentLink = ({ href, ...rest }: CatalogAdContentLinkProps) =>
    href.startsWith('http') ? (
        <Link
            href={href}
            {...rest}
        />
    ) : (
        <RouteLink
            to={href}
            {...rest}
        />
    );

const CatalogAd = (props: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const { adUnit, children, className, scrolled, stick, stickBottom, trackClick } = props;
    const { catalogId, catalogTitle, destination, imageUrl1, imageUrl2, imageUrl3, logoId, saleStartTs, sellerName } =
        adUnit;

    // If only the second image exists we're going to treat it really special, it will get to be the only image.
    const customImage = imageUrl2 && !imageUrl1 && !imageUrl3;

    // we want to make sure that we only use truthy imageUrls so we filter;
    const images = React.useMemo(() => {
        return [imageUrl1, imageUrl2, imageUrl3].filter((image) => Boolean(image));
    }, [imageUrl1, imageUrl2, imageUrl3]);

    const href = destination || getCatalogUrl(catalogId, catalogTitle);

    const mapImages = React.useCallback(
        (url: string, index: number) => (
            <div
                className="mb-[5px] flex h-[120px] w-[162px] items-center justify-center overflow-hidden"
                key={`at-ad-${url}`}
            >
                <img
                    alt={`${catalogTitle} (${index + 1} of ${images.length})`}
                    className="max-w-[162px] object-contain"
                    height="122"
                    loading="lazy"
                    src={addImageParams({ url, width: 162 })}
                    width="162"
                />
            </div>
        ),
        [catalogTitle, images.length]
    );

    let style: { [attribute: string]: string | number } = stick && scrolled ? { position: 'fixed', top: 0 } : {};
    if (stickBottom && scrolled) {
        style = { bottom: 0, position: 'absolute' };
    }

    const clientSafeDate = useSSRFriendlyDate(saleStartTs);

    const loadCatalog = React.useCallback(() => {
        dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] }));
    }, [catalogId, dispatch]);

    const hoverIntentRef = useHoverIntent({
        interval: 50,
        onMouseOut: noop,
        onMouseOver: loadCatalog,
        sensitivity: 5,
        timeout: 50,
    });

    return (
        <div
            className="bg-background-white relative mt-5 flex flex-col items-center justify-start"
            data-testid="catalog-ad-hover-intent"
            onClick={trackClick}
            ref={hoverIntentRef}
            style={style}
        >
            {customImage ? (
                <Link
                    aria-label={`Explore ${catalogTitle}`}
                    className="block h-[641px] w-[180px]"
                    data-testid="catalog-ad-content"
                    href={destination || getCatalogUrl(catalogId, catalogTitle)}
                    target={!destination.startsWith('http') ? '_self' : '_blank'}
                >
                    <img
                        alt={catalogTitle}
                        className="block h-[641px] w-[180px]"
                        data-testid="catalog-ad-custom-image-link"
                        loading="lazy"
                        src={addImageParams({ url: imageUrl2, width: 162 })}
                    />
                </Link>
            ) : (
                <div
                    className={cn(
                        'mb-2xs flex w-[180px] flex-col border border-solid border-border-secondary p-xs pb-5 text-center',
                        className
                    )}
                    data-testid="catalog-ad-content"
                >
                    <CatalogAdContentLink
                        aria-label={`Explore ${catalogTitle}`}
                        className="mb-[10px] flex items-center justify-center"
                        data-testid="catalog-ad-title"
                        href={href}
                    >
                        {logoId && (
                            <img
                                alt={sellerName}
                                className="max-h-[120px]"
                                data-testid="catalog-ad-logo"
                                loading="lazy"
                                src={getAuctioneerLogoUrl(logoId, 162)}
                            />
                        )}
                    </CatalogAdContentLink>
                    <CatalogAdContentLink
                        className="relative mb-[10px] box-content block"
                        href={href}
                    >
                        {images.map(mapImages)}
                    </CatalogAdContentLink>
                    <CatalogAdContentLink
                        aria-label={`Explore ${catalogTitle}`}
                        className="text-left font-secondary text-sm leading-normal break-words text-text-primary no-underline hover:no-underline"
                        href={href}
                        size="xs"
                    >
                        {safelyRender({ html: catalogTitle })}
                    </CatalogAdContentLink>
                    <span className="mb-[10px] text-left font-primary text-xs text-text-secondary">
                        {clientSafeDate}
                    </span>
                    <RouteButton
                        aria-label={`Explore ${catalogTitle}`}
                        className="w-full"
                        data-testid="catalog-ad-explore-button"
                        size="sm"
                        to={href}
                        variant="outlined"
                    >
                        {formatMessage({ id: 'explore_auction' })}
                    </RouteButton>
                </div>
            )}
            <div className="w-[180px] text-xs">
                <FormattedMessage id="advertisement" />{' '}
                <Link
                    aria-label="How to see your own ad"
                    data-testid="catalog-ad-your-ad-link"
                    href={getAdQuestionUrl()}
                    size="xs"
                >
                    <FormattedMessage id="your_ad_here" />
                </Link>
            </div>
            {children}
        </div>
    );
};

export default React.memo<Props>(CatalogAd, isEqual);
